import { debounce } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { useTerritoryCode } from './useTerritoryCode';
import { userSearchService } from '../services/userSearchService';

export const useUserSearch = (
  role,
  maxResultCount = 10,
  debounceTime = 250,
  validators = []
) => {
  const [usersFound, setUsersFound] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lastSearch, setLastSearch] = useState('');
  const lastSearchRef = useRef(lastSearch);
  const territoryCode = useTerritoryCode();
  lastSearchRef.current = lastSearch;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchUsers = useCallback(
    userSearchService(role, maxResultCount, territoryCode),
    [role, maxResultCount]
  );

  const debouncedSearchUsers = debounce(async (searchText = '') => {
    setLastSearch(searchText);
    setIsLoading(true);
    setHasError(false);
    const isValid = validators.every((x) => {
      return x(searchText);
    });

    if (!isValid) {
      if (lastSearchRef.current === searchText) {
        setUsersFound([]);
        setIsLoading(false);
      }
      return;
    }

    let users = [];
    try {
      users = await searchUsers(searchText);
    } catch (e) {
      if (e.message !== 'tokenCanceled') setHasError(true);
    }
    if (lastSearchRef.current === searchText) {
      setIsLoading(false);
      setUsersFound(users);
    }
  }, debounceTime);

  const clearUsers = () => {
    setUsersFound([]);
    setIsLoading(false);
    setHasError(false);
  };

  return {
    usersFound,
    searchUsers: debouncedSearchUsers,
    clearUsers,
    isLoading,
    hasError,
  };
};
